import React, { useContext } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Image,
  IconButton,
  useDisclosure,
  Collapse,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Text
} from '@chakra-ui/react';
import {
  FaBars,
  FaTimes,
  FaSignInAlt,
  FaUser,
  FaTachometerAlt,
  FaSignOutAlt,
  FaHome,
  FaBox,
  FaEye,
  FaEnvelope
} from 'react-icons/fa';
import { ChevronDownIcon } from '@chakra-ui/icons';
import AuthContext from '../services/AuthContext';

const Header = () => {
  const { isOpen, onToggle } = useDisclosure();
  const location = useLocation();
  const { user, logout } = useContext(AuthContext);
  
  // Colors
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';

  const handleLogout = () => {
    logout();
  };

  const navigationItems = [
    { name: 'الرئيسية', path: '/', icon: FaHome },
    { name: 'عروضنا', path: '/packs', icon: FaBox },
    { name: 'حول المنصة', path: '/about', icon: FaEye },
    { name: 'اتصل بنا', path: '/contact', icon: FaEnvelope }
  ];

  return (
    <Box 
      bg={secondaryColor} 
      color="white" 
      py={2} 
      borderBottom={`2px solid ${primaryColor}`}
      position="sticky"
      top={0}
      zIndex="sticky"
      boxShadow="md"
      dir="rtl"
    >
      <Container maxW="8xl">
        <Flex justify="space-between" align="center">
          {/* Logo */}
          <RouterLink to="/">
            <HStack spacing={3}>
              <Image src="https://i.imgur.com/EFLnpkN.png" alt="منصة كوبرا التعليمية" h="50px" />
            </HStack>
          </RouterLink>

          {/* Desktop Navigation */}
          <HStack spacing={8} display={{ base: 'none', md: 'flex' }}>
            {navigationItems.map((item) => (
              <Button 
                key={item.path}
                as={RouterLink}
                to={item.path}
                variant="ghost" 
                color="white" 
                _hover={{ color: primaryColor, bg: 'rgba(242, 201, 25, 0.1)' }}
                fontWeight={location.pathname === item.path ? 'bold' : 'normal'}
                rightIcon={<item.icon />} // Changed leftIcon to rightIcon for RTL
                fontFamily="Cairo, sans-serif"
              >
                {item.name}
              </Button>
            ))}
          </HStack>

          {/* Mobile Menu Button */}
          <IconButton
            display={{ base: 'flex', md: 'none' }}
            onClick={onToggle}
            icon={isOpen ? <FaTimes /> : <FaBars />}
            variant="ghost"
            aria-label="تبديل القائمة"
            color="white"
            _hover={{ bg: 'rgba(242, 201, 25, 0.1)' }}
          />

          {/* User Actions */}
          <HStack spacing={4} display={{ base: 'none', md: 'flex' }}>
            {user ? (
              <Menu>
                <MenuButton
                  as={Button}
                  variant="ghost"
                  color="white"
                  _hover={{ bg: 'rgba(242, 201, 25, 0.1)' }}
                  leftIcon={<ChevronDownIcon />} // Changed rightIcon to leftIcon for RTL
                >
                  <HStack spacing={2} flexDirection="row-reverse"> {/* Added flexDirection for RTL */}
                    <Avatar size="sm" name={user.name} />
                    <Text fontFamily="Cairo, sans-serif">{user.name}</Text>
                  </HStack>
                </MenuButton>
                <MenuList bg="white" color={secondaryColor} border={`1px solid ${primaryColor}`}>
                  <MenuItem 
                    as={RouterLink}
                    to={user.role==="instructor"?"/instructor/courses":user.role==="admin"?"/admin/users":"/courses"}
                    icon={<FaTachometerAlt />}
                    _hover={{ bg: `${primaryColor}20` }}
                    fontFamily="Cairo, sans-serif"
                  >
                    لوحة التحكم
                  </MenuItem>
               
                  <MenuItem 
                    onClick={handleLogout}
                    icon={<FaSignOutAlt />}
                    _hover={{ bg: 'red.50', color: 'red.500' }}
                    fontFamily="Cairo, sans-serif"
                  >
                    تسجيل الخروج
                  </MenuItem>
                </MenuList>
              </Menu>
            ) : (
              <Button 
                as={RouterLink}
                to="/login"
                colorScheme="yellow" 
                variant="solid" 
                bg={primaryColor}
                color={secondaryColor}
                rightIcon={<FaSignInAlt />} // Changed leftIcon to rightIcon for RTL
                _hover={{ bg: '#d4b016' }}
                fontFamily="Cairo, sans-serif"
              >
                تسجيل الدخول
              </Button>
            )}
          </HStack>
        </Flex>

        {/* Mobile Navigation */}
        <Collapse in={isOpen} animateOpacity>
          <VStack 
            spacing={4} 
            py={4} 
            align="stretch"
            display={{ md: 'none' }}
          >
            {navigationItems.map((item) => (
              <Button 
                key={item.path}
                as={RouterLink}
                to={item.path}
                variant="ghost" 
                color="white" 
                _hover={{ bg: 'rgba(242, 201, 25, 0.1)' }}
                justifyContent="flex-end" // Changed justifyContent to flex-end for RTL
                rightIcon={<item.icon />} // Changed leftIcon to rightIcon for RTL
                fontFamily="Cairo, sans-serif"
              >
                {item.name}
              </Button>
            ))}
            
            {user ? (
              <>
                <Button 
                  as={RouterLink}
                  to={user.role==="instructor"?"/instructor/courses":user.role==="admin"?"/admin/users":"/courses"}
                  variant="ghost" 
                  color="white" 
                  _hover={{ bg: 'rgba(242, 201, 25, 0.1)' }}
                  justifyContent="flex-end" // Changed justifyContent to flex-end for RTL
                  rightIcon={<FaTachometerAlt />} // Changed leftIcon to rightIcon for RTL
                  fontFamily="Cairo, sans-serif"
                >
                  لوحة التحكم
                </Button>
           
                <Button 
                  onClick={handleLogout}
                  variant="ghost" 
                  color="white" 
                  _hover={{ bg: 'red.500' }}
                  justifyContent="flex-end" // Changed justifyContent to flex-end for RTL
                  rightIcon={<FaSignOutAlt />} // Changed leftIcon to rightIcon for RTL
                  fontFamily="Cairo, sans-serif"
                >
                  تسجيل الخروج
                </Button>
              </>
            ) : (
              <Button 
                as={RouterLink}
                to="/login"
                colorScheme="yellow" 
                variant="solid" 
                bg={primaryColor}
                color={secondaryColor}
                rightIcon={<FaSignInAlt />} // Changed leftIcon to rightIcon for RTL
                fontFamily="Cairo, sans-serif"
              >
                تسجيل الدخول
              </Button>
            )}
          </VStack>
        </Collapse>
      </Container>
    </Box>
  );
};

export default Header;


